import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const AgencyContext = createContext(null);

function hexToHsl(hex) {
  if (!hex) return null;
  const h = hex.replace("#", "");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hh,
    s,
    l = (max + min) / 2;
  if (max === min) {
    hh = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hh = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        hh = (b - r) / d + 2;
        break;
      default:
        hh = (r - g) / d + 4;
    }
    hh /= 6;
  }
  return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function applyBrandColor(hex) {
  const hsl = hexToHsl(hex);
  if (hsl) {
    document.documentElement.style.setProperty("--primary", hsl);
    document.documentElement.style.setProperty("--ring", hsl);
    document.documentElement.style.setProperty("--primary-hex", hex);
  }
}

export function AgencyProvider({ children }) {
  const { user } = useAuth();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadAgency = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get("/agencies/current");
      setAgency(data);
      applyBrandColor(data.brand_primary_color);
    } catch {
      setAgency(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && typeof user === "object") {
      loadAgency();
    }
  }, [user, loadAgency]);

  const updateAgency = (next) => {
    setAgency(next);
    if (next?.brand_primary_color) applyBrandColor(next.brand_primary_color);
  };

  return (
    <AgencyContext.Provider value={{ agency, loading, loadAgency, updateAgency }}>
      {children}
    </AgencyContext.Provider>
  );
}

export function useAgency() {
  const ctx = useContext(AgencyContext);
  if (!ctx) throw new Error("useAgency must be used inside <AgencyProvider>");
  return ctx;
}
